import React, { useState } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ReferenceLine
} from 'recharts';
import './GastosChart.css';

const MONTHS = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
const fmt = v => v.toLocaleString('pt-BR', { style:'currency', currency:'BRL' });

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      <div className="tooltip-label">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="tooltip-item" style={{ color: p.color }}>
          <span className="tooltip-dot" style={{ background: p.color }} />
          {p.name}: <strong>{fmt(p.value)}</strong>
        </div>
      ))}
    </div>
  );
};

export default function GastosChart({ contas, lancamentos, metas, ano }) {
  const [chartType, setChartType] = useState('bar');

  function getVal(contaId, mes) {
    return lancamentos.find(l => l.conta_id === contaId && l.mes === mes + 1)?.valor || 0;
  }

  // Data por mês
  const data = MONTHS.map((m, i) => {
    const cartao = contas.filter(c => c.tipo === 'cartao').reduce((a, c) => a + getVal(c.id, i), 0);
    const fixo   = contas.filter(c => c.tipo === 'fixo').reduce((a, c) => a + getVal(c.id, i), 0);
    const outro  = contas.filter(c => c.tipo === 'outro').reduce((a, c) => a + getVal(c.id, i), 0);
    const total  = cartao + fixo + outro;
    const meta   = metas.find(m2 => m2.mes === i + 1)?.valor_referencia || 0;
    return { mes: m, Cartão: cartao, Fixos: fixo, Outros: outro, Total: total, 'Limite Mensal': meta };
  });

  // Pizza por categoria (mês atual)
  const now = new Date().getMonth();
  const pieData = [
    { name: 'Cartão', value: data[now].Cartão, color: '#00d4aa' },
    { name: 'Fixos',  value: data[now].Fixos,  color: '#4f8ef7' },
    { name: 'Outros', value: data[now].Outros, color: '#f5a623' },
  ].filter(d => d.value > 0);

  // Top contas do ano
  const topContas = contas.map(c => ({
    nome: c.nome,
    tipo: c.tipo,
    total: MONTHS.reduce((acc, _, i) => acc + getVal(c.id, i), 0),
  })).filter(c => c.total > 0).sort((a, b) => b.total - a.total).slice(0, 8);

  const topData = topContas.map(c => ({ name: c.nome.length > 18 ? c.nome.slice(0,16)+'…' : c.nome, Total: c.total }));

  return (
    <div className="charts-wrap fade-in">
      <div className="chart-toolbar">
        <h3 className="chart-title">Análise Anual — {ano}</h3>
        <div className="chart-type-btns">
          <button className={`btn btn-sm ${chartType === 'bar' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setChartType('bar')}>
            ▐▌ Barras
          </button>
          <button className={`btn btn-sm ${chartType === 'line' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setChartType('line')}>
            ⟁ Linha
          </button>
        </div>
      </div>

      {/* Main chart — evolução mensal */}
      <div className="chart-card">
        <div className="chart-card-header">
          <span className="chart-card-title">Evolução de Gastos por Mês</span>
          <span className="chart-card-sub text-muted text-xs">Comparativo com limite mensal</span>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          {chartType === 'bar' ? (
            <BarChart data={data} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="mes" tick={{ fill: '#4a5e74', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={v => `R$${(v/1000).toFixed(0)}k`} tick={{ fill: '#4a5e74', fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
              <Legend wrapperStyle={{ fontSize: 12, color: '#8a9ab0' }} />
              <Bar dataKey="Cartão" fill="#00d4aa" radius={[4,4,0,0]} maxBarSize={32} />
              <Bar dataKey="Fixos" fill="#4f8ef7" radius={[4,4,0,0]} maxBarSize={32} />
              <Bar dataKey="Outros" fill="#f5a623" radius={[4,4,0,0]} maxBarSize={32} />
            </BarChart>
          ) : (
            <LineChart data={data} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="mes" tick={{ fill: '#4a5e74', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={v => `R$${(v/1000).toFixed(0)}k`} tick={{ fill: '#4a5e74', fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12, color: '#8a9ab0' }} />
              <Line type="monotone" dataKey="Total" stroke="#00d4aa" strokeWidth={2.5} dot={{ fill: '#00d4aa', r: 4 }} activeDot={{ r: 6 }} />
              <Line type="monotone" dataKey="Limite Mensal" stroke="#f5a623" strokeWidth={2} strokeDasharray="5 4" dot={false} />
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>

      <div className="charts-row">
        {/* Total x Meta mensal */}
        <div className="chart-card chart-card-half">
          <div className="chart-card-header">
            <span className="chart-card-title">Total vs. Limite Mensal</span>
            <span className="chart-card-sub text-muted text-xs">Linha laranja = referência</span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="mes" tick={{ fill: '#4a5e74', fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={v => `R$${(v/1000).toFixed(0)}k`} tick={{ fill: '#4a5e74', fontSize: 10 }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
              <Legend wrapperStyle={{ fontSize: 11, color: '#8a9ab0' }} />
              <Bar dataKey="Total" fill="#00d4aa" radius={[4,4,0,0]} maxBarSize={28} />
              <Line type="monotone" dataKey="Limite Mensal" stroke="#f5a623" strokeWidth={2} dot={false} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Top contas do ano */}
        <div className="chart-card chart-card-half">
          <div className="chart-card-header">
            <span className="chart-card-title">Top Contas no Ano</span>
            <span className="chart-card-sub text-muted text-xs">Soma acumulada {ano}</span>
          </div>
          {topData.length === 0 ? (
            <div className="chart-empty">Nenhum lançamento ainda</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={topData} layout="vertical" margin={{ top: 0, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" horizontal={false} />
                <XAxis type="number" tickFormatter={v => `R$${(v/1000).toFixed(0)}k`} tick={{ fill: '#4a5e74', fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" width={110} tick={{ fill: '#8a9ab0', fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
                <Bar dataKey="Total" fill="#4f8ef7" radius={[0,4,4,0]} maxBarSize={20} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Resumo anual */}
      <div className="chart-card">
        <div className="chart-card-header">
          <span className="chart-card-title">Resumo Anual</span>
        </div>
        <div className="annual-summary">
          {['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'].map((m, i) => {
            const d = data[i];
            const dif = d['Limite Mensal'] - d.Total;
            const hasData = d.Total > 0;
            return (
              <div key={i} className={`annual-item ${!hasData ? 'empty' : dif >= 0 ? 'pos' : 'neg'}`}>
                <div className="annual-mes">{m}</div>
                <div className="annual-total font-mono">{hasData ? fmt(d.Total) : '—'}</div>
                {hasData && d['Limite Mensal'] > 0 && (
                  <div className={`annual-dif font-mono ${dif >= 0 ? 'pos' : 'neg'}`}>
                    {dif >= 0 ? '▼' : '▲'} {fmt(Math.abs(dif))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
